import React from 'react';
import { Textarea } from '@amzn/awsui-components-core';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import ReactMarkdown from 'react-markdown';
import { DateTime } from 'luxon';
import { MeetingComment, TimeSlotVote } from '../../interfaces/interface';
import { EmployeePopoverCard } from '../../components/attendees/EmployeePopoverCard';
import './CommentItem.scss';

interface CommentItemProps {
  comment: MeetingComment;
  currentUserAlias: string;
  commentVote?: TimeSlotVote;
  isEditing: boolean;
  editDraft: string;
  onEditDraftChange: (value: string) => void;
  onSaveEdit: (commentId: string) => void;
  onCancelEdit: () => void;
  onStartEdit: (commentId: string, content: string) => void;
  editDisabled?: boolean;
  showThreadLink?: boolean;
  onOpenThread?: (comment: MeetingComment) => void;
  replyCount?: number;
  showReplyLink?: boolean;
  onReply?: (comment: MeetingComment) => void;
  /** "detailed" = 2-line layout (alias + time on row 1, content on row 2) used inside thread modal */
  variant?: 'inline' | 'detailed';
  children?: React.ReactNode;
}

function voteEmoji(vote?: 'up' | 'down', hasComment?: boolean): string {
  if (vote === 'up') return '👍';
  if (vote === 'down') return '👎';
  if (hasComment) return '⌛';
  return '';
}

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  currentUserAlias,
  commentVote,
  isEditing,
  editDraft,
  onEditDraftChange,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  editDisabled = false,
  showThreadLink = false,
  onOpenThread,
  replyCount = 0,
  showReplyLink = false,
  onReply,
  variant = 'inline',
  children
}) => {
  const isOwnComment = comment.loginName.toLowerCase() === currentUserAlias.toLowerCase();
  const emoji = voteEmoji(commentVote?.vote, true);
  const relativeTime = comment.updatedAt
    ? `edited ${DateTime.fromISO(comment.updatedAt).toRelative() ?? ''}`
    : DateTime.fromISO(comment.createdAt).toRelative() ?? '';

  if (variant === 'detailed') {
    return (
      <div className="ts-comment-node">
        <div className="ts-comment-detailed">
          <div className="ts-comment-detailed-header">
            <EmployeePopoverCard loginName={comment.loginName}>
              <Avatar username={comment.loginName} size={AvatarSize.ExtraSmall} imageAltText={comment.employeeName || comment.loginName} />
            </EmployeePopoverCard>
            <span className="ts-comment-alias-inline">{comment.employeeName || comment.loginName}</span>
            <span className="ts-comment-time">{relativeTime}</span>
            {!isEditing && (
              <>
                {isOwnComment && (
                  <>
                    <span className="ts-comment-separator">·</span>
                    <button className="ts-comment-action-btn" onClick={() => onStartEdit(comment.commentId, comment.content)} disabled={editDisabled}>
                      Edit
                    </button>
                  </>
                )}
                {showReplyLink && onReply && (
                  <>
                    <span className="ts-comment-separator">·</span>
                    <button className="ts-comment-action-btn" onClick={() => onReply(comment)}>
                      Reply
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          <div className="ts-comment-detailed-content-wrapper">
            {isEditing ? (
              <div className="ts-comment-edit-row">
                <Textarea value={editDraft} onChange={({ detail }) => onEditDraftChange(detail.value)} rows={2} autoFocus />
                <button className="ts-comment-icon-btn ts-comment-icon-btn--confirm" onClick={() => onSaveEdit(comment.commentId)} aria-label="Save">
                  ✔️
                </button>
                <button className="ts-comment-icon-btn ts-comment-icon-btn--cancel" onClick={onCancelEdit} aria-label="Cancel">
                  ❌
                </button>
              </div>
            ) : (
              <div className="ts-comment-detailed-content">
                {comment.content?.trim() ? (
                  <ReactMarkdown className="ts-comment-markdown">{comment.content}</ReactMarkdown>
                ) : (
                  <em className="ts-comment-text">—</em>
                )}
              </div>
            )}
          </div>
        </div>
        {children && <div className="ts-comment-children">{children}</div>}
      </div>
    );
  }

  // Inline variant (compact, single-line — used in the comment list)
  return (
    <div className="ts-comment-node">
      <div className="ts-comment-row">
        <span className="ts-comment-vote-emoji">{emoji}</span>
        <EmployeePopoverCard loginName={comment.loginName}>
          <Avatar username={comment.loginName} size={AvatarSize.ExtraSmall} imageAltText={comment.employeeName || comment.loginName} />
        </EmployeePopoverCard>
        <span className="ts-comment-alias-inline">{comment.employeeName || comment.loginName}</span>
        <span className="ts-comment-row-divider" />

        {isEditing ? (
          <div className="ts-comment-edit-row">
            <Textarea value={editDraft} onChange={({ detail }) => onEditDraftChange(detail.value)} autoFocus />
            <button className="ts-comment-icon-btn ts-comment-icon-btn--confirm" onClick={() => onSaveEdit(comment.commentId)} aria-label="Save">
              ✔️
            </button>
            <button className="ts-comment-icon-btn ts-comment-icon-btn--cancel" onClick={onCancelEdit} aria-label="Cancel">
              ❌
            </button>
          </div>
        ) : (
          <>
            <div className="ts-comment-inline">
              {comment.content?.trim() ? (
                <ReactMarkdown className="ts-comment-markdown ts-comment-markdown--inline">{comment.content}</ReactMarkdown>
              ) : (
                <em className="ts-comment-text">—</em>
              )}
            </div>
            <span className="ts-comment-time-inline">{relativeTime}</span>
          </>
        )}

        {!isEditing && (
          <div className="ts-comment-meta">
            <span className="ts-comment-separator">·</span>
            {showThreadLink && onOpenThread && (
              <button className="ts-comment-action-btn" onClick={() => onOpenThread(comment)}>
                Thread{replyCount > 0 ? ` (${replyCount})` : ''}
              </button>
            )}
            {isOwnComment && (
              <>
                <span className="ts-comment-separator">·</span>
                <button className="ts-comment-action-btn" onClick={() => onStartEdit(comment.commentId, comment.content)} disabled={editDisabled}>
                  Edit
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {children && <div className="ts-comment-children">{children}</div>}
    </div>
  );
};
